import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SubscriptionManagement = ({ user, onDataChange }) => {
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'packages', 'billing'
  const [showCancelModal, setShowCancelModal] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(null);

  // Mock subscription data - in real app this would come from API
  const subscriptions = {
    active: [
      {
        id: 1,
        name: 'Wellness Plus Monthly',
        type: 'membership',
        status: 'active',
        price: 89.99,
        billing: 'monthly',
        nextBilling: '2024-02-15',
        features: [
          '4 EES sessions per month',
          '2 meditation retreat sessions',
          'Unlimited wellness app access',
          'Priority booking',
          '20% discount on additional sessions'
        ],
        usage: {
          eesSessions: { used: 2, total: 4 },
          meditationSessions: { used: 1, total: 2 },
          monthlyPeriod: 'Jan 15 - Feb 15'
        }
      }
    ],
    packages: [
      {
        id: 2,
        name: '10 EES Session Package',
        type: 'package',
        status: 'active',
        totalSessions: 10,
        usedSessions: 3,
        purchaseDate: '2024-01-10',
        expiryDate: '2024-07-10',
        price: 1200,
        perSessionPrice: 120,
        centers: ['Harmony Wellness Center', 'Quantum Healing Center']
      },
      {
        id: 3,
        name: 'Sound Healing Package',
        type: 'package',
        status: 'expired',
        totalSessions: 5,
        usedSessions: 5,
        purchaseDate: '2023-11-01',
        expiryDate: '2024-01-01',
        price: 350,
        perSessionPrice: 70
      }
    ]
  };

  const availablePlans = [
    {
      id: 'basic',
      name: 'Wellness Basic',
      price: 49.99,
      billing: 'monthly',
      popular: false,
      features: [
        '2 EES sessions per month',
        '1 meditation session',
        'Basic wellness app access',
        'Standard booking',
        '10% discount on additional sessions'
      ]
    },
    {
      id: 'plus',
      name: 'Wellness Plus',
      price: 89.99,
      billing: 'monthly',
      popular: true,
      features: [
        '4 EES sessions per month',
        '2 meditation retreat sessions',
        'Unlimited wellness app access',
        'Priority booking',
        '20% discount on additional sessions',
        'Free wellness consultation'
      ]
    },
    {
      id: 'premium',
      name: 'Wellness Premium',
      price: 149.99,
      billing: 'monthly',
      popular: false,
      features: [
        'Unlimited EES sessions',
        'Unlimited retreat access',
        'Premium wellness app features',
        'VIP booking priority',
        '30% discount on additional services',
        'Monthly wellness consultation',
        'Personalized wellness plan'
      ]
    }
  ];

  const sessionPackages = [
    {
      id: 'ees-5',
      name: '5 EES Sessions',
      sessions: 5,
      price: 550,
      perSessionPrice: 110,
      savings: 50,
      validity: '6 months',
      description: 'Perfect starter package for experiencing EES benefits'
    },
    {
      id: 'ees-10',
      name: '10 EES Sessions',
      sessions: 10,
      price: 1000,
      perSessionPrice: 100,
      savings: 200,
      validity: '12 months',
      description: 'Most popular package for regular wellness routine',
      popular: true
    },
    {
      id: 'meditation-8',
      name: '8 Meditation Sessions',
      sessions: 8,
      price: 320,
      perSessionPrice: 40,
      savings: 40,
      validity: '8 months',
      description: 'Comprehensive meditation journey package'
    }
  ];

  const billingHistory = [
    {
      id: 1,
      date: '2024-01-15',
      description: 'Wellness Plus Monthly',
      amount: 89.99,
      status: 'paid',
      method: '•••• 4521'
    },
    {
      id: 2,
      date: '2024-01-10',
      description: '10 EES Session Package',
      amount: 1200.00,
      status: 'paid',
      method: '•••• 4521'
    },
    {
      id: 3,
      date: '2023-12-15',
      description: 'Wellness Plus Monthly',
      amount: 89.99,
      status: 'paid',
      method: '•••• 4521'
    }
  ];

  const handleCancelSubscription = (subscription) => {
    setShowCancelModal(subscription);
  };

  const handleUpgradePlan = (plan) => {
    setShowUpgradeModal(plan);
  };

  const confirmCancel = () => {
    // In real app, this would make API call to cancel subscription
    console.log('Cancelling subscription:', showCancelModal);
    setShowCancelModal(null);
    onDataChange?.();
  };

  const confirmUpgrade = () => {
    // In real app, this would make API call to upgrade plan
    console.log('Upgrading to plan:', showUpgradeModal);
    setShowUpgradeModal(null);
    onDataChange?.();
  };

  const renderActiveSubscriptions = () => (
    <div className="space-y-6">
      {/* Active Memberships */}
      {subscriptions.active.map((subscription) => (
        <div key={subscription.id} className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
          <div className="px-6 py-4 border-b border-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">{subscription.name}</h3>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full text-xs font-medium">
                    Active
                  </span>
                  <span className="text-sm text-slate-600">
                    Next billing: {new Date(subscription.nextBilling).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">
                  ${subscription.price}
                </div>
                <div className="text-sm text-slate-600">per {subscription.billing}</div>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Features */}
              <div>
                <h4 className="font-medium text-slate-800 mb-3">Plan Features</h4>
                <ul className="space-y-2">
                  {subscription.features.map((feature, index) => (
                    <li key={index} className="flex items-center space-x-2 text-sm text-slate-600">
                      <Icon name="Check" size={14} className="text-emerald-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Usage */}
              <div>
                <h4 className="font-medium text-slate-800 mb-3">
                  Current Usage ({subscription.usage.monthlyPeriod})
                </h4>
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-slate-600">EES Sessions</span>
                      <span className="font-medium">
                        {subscription.usage.eesSessions.used}/{subscription.usage.eesSessions.total}
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${(subscription.usage.eesSessions.used / subscription.usage.eesSessions.total) * 100}%`
                        }}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-slate-600">Meditation Sessions</span>
                      <span className="font-medium">
                        {subscription.usage.meditationSessions.used}/{subscription.usage.meditationSessions.total}
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${(subscription.usage.meditationSessions.used / subscription.usage.meditationSessions.total) * 100}%`
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 pt-6 border-t border-blue-100 flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => handleUpgradePlan(subscription)}
                className="flex-1 sm:flex-none bg-blue-600 text-white px-6 py-2 rounded-md font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Upgrade Plan
              </button>
              <button className="flex-1 sm:flex-none border border-blue-200 text-blue-600 px-6 py-2 rounded-md font-medium hover:bg-blue-50 transition-colors duration-200">
                Manage Payment
              </button>
              <button
                onClick={() => handleCancelSubscription(subscription)}
                className="flex-1 sm:flex-none border border-red-200 text-red-600 px-6 py-2 rounded-md font-medium hover:bg-red-50 transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ))}
      
      {/* Active Packages */}
      {subscriptions.packages.filter(pkg => pkg.status === 'active').map((pkg) => (
        <div key={pkg.id} className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
          <div className="px-6 py-4 border-b border-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">{pkg.name}</h3>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                    Session Package
                  </span>
                  <span className="text-sm text-slate-600">
                    Expires: {new Date(pkg.expiryDate).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xl font-bold text-slate-800">
                  {pkg.usedSessions}/{pkg.totalSessions}
                </div>
                <div className="text-sm text-slate-600">sessions used</div>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-slate-800 mb-3">Package Details</h4>
                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex justify-between">
                    <span>Total Value:</span>
                    <span className="font-medium">${pkg.price}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Per Session:</span>
                    <span className="font-medium">${pkg.perSessionPrice}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Purchased:</span>
                    <span>{new Date(pkg.purchaseDate).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Remaining Sessions:</span>
                    <span className="font-medium text-blue-600">
                      {pkg.totalSessions - pkg.usedSessions}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-slate-800 mb-3">Progress</h4>
                <div className="mb-4">
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div
                      className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                      style={{
                        width: `${(pkg.usedSessions / pkg.totalSessions) * 100}%`
                      }}
                    />
                  </div>
                </div>
                
                {pkg.centers && (
                  <div>
                    <h5 className="text-sm font-medium text-slate-700 mb-2">Available at:</h5>
                    <div className="space-y-1">
                      {pkg.centers.map((center, index) => (
                        <div key={index} className="text-sm text-slate-600 flex items-center">
                          <Icon name="MapPin" size={12} className="mr-2" />
                          {center}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-6 pt-6 border-t border-blue-100 flex space-x-3">
              <button className="bg-blue-600 text-white px-6 py-2 rounded-md font-medium hover:bg-blue-700 transition-colors duration-200">
                Book Session
              </button>
              <button className="border border-blue-200 text-blue-600 px-6 py-2 rounded-md font-medium hover:bg-blue-50 transition-colors duration-200">
                View Details
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderAvailablePackages = () => (
    <div className="space-y-8">
      {/* Monthly Plans */}
      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-6">Monthly Wellness Plans</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {availablePlans.map((plan) => (
            <div
              key={plan.id}
              className={`bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border transition-all duration-300 hover:shadow-xl ${
                plan.popular 
                  ? 'border-blue-500 ring-2 ring-blue-500 ring-opacity-20' :'border-blue-100 hover:border-blue-300'
              }`}
            >
              {plan.popular && (
                <div className="bg-blue-500 text-white text-center py-2 text-sm font-medium rounded-t-lg">
                  Most Popular
                </div>
              )}
              
              <div className="p-6">
                <h4 className="text-xl font-semibold text-slate-800 mb-2">{plan.name}</h4>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-blue-600">${plan.price}</span>
                  <span className="text-slate-600">/{plan.billing}</span>
                </div>
                
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2 text-sm text-slate-600">
                      <Icon name="Check" size={14} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button
                  onClick={() => handleUpgradePlan(plan)}
                  className={`w-full py-2 px-4 rounded-md font-medium transition-colors duration-200 ${
                    plan.popular
                      ? 'bg-blue-600 text-white hover:bg-blue-700' :'border border-blue-600 text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  {subscriptions.active.length > 0 ? 'Switch Plan' : 'Subscribe'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Session Packages */}
      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-6">Session Packages</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {sessionPackages.map((pkg) => (
            <div
              key={pkg.id}
              className={`bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border transition-all duration-300 hover:shadow-xl ${
                pkg.popular 
                  ? 'border-emerald-500 ring-2 ring-emerald-500 ring-opacity-20' :'border-blue-100 hover:border-blue-300'
              }`}
            >
              {pkg.popular && (
                <div className="bg-emerald-500 text-white text-center py-2 text-sm font-medium rounded-t-lg">
                  Best Value
                </div>
              )}
              
              <div className="p-6">
                <h4 className="text-xl font-semibold text-slate-800 mb-2">{pkg.name}</h4>
                <p className="text-sm text-slate-600 mb-4">{pkg.description}</p>
                
                <div className="space-y-2 text-sm text-slate-600 mb-4">
                  <div className="flex justify-between">
                    <span>Total Sessions:</span>
                    <span className="font-medium">{pkg.sessions}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Per Session:</span>
                    <span className="font-medium">${pkg.perSessionPrice}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>You Save:</span>
                    <span className="font-medium text-emerald-600">${pkg.savings}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Valid For:</span>
                    <span className="font-medium">{pkg.validity}</span>
                  </div>
                </div>
                
                <div className="text-center mb-4">
                  <span className="text-2xl font-bold text-blue-600">${pkg.price}</span>
                </div>
                
                <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-md font-medium hover:bg-blue-700 transition-colors duration-200">
                  Purchase Package
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBillingHistory = () => (
    <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
      <div className="px-6 py-4 border-b border-blue-100">
        <h3 className="text-lg font-semibold text-slate-800">Billing History</h3>
        <p className="text-sm text-slate-600 mt-1">View your past payments and download receipts</p>
      </div>
      
      <div className="p-6">
        <div className="space-y-4">
          {billingHistory.map((bill) => (
            <div key={bill.id} className="flex items-center justify-between p-4 bg-white rounded-lg border border-blue-200">
              <div>
                <h4 className="font-medium text-slate-800">{bill.description}</h4>
                <div className="flex items-center space-x-3 text-sm text-slate-600">
                  <span>{new Date(bill.date).toLocaleDateString()}</span>
                  <span>•</span>
                  <span>Paid with {bill.method}</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <div className="font-medium text-slate-800">${bill.amount}</div>
                  <div className={`text-xs px-2 py-1 rounded-full ${
                    bill.status === 'paid' ?'bg-emerald-100 text-emerald-800' :'bg-red-100 text-red-800'
                  }`}>
                    {bill.status}
                  </div>
                </div>
                
                <button className="text-blue-600 hover:text-blue-700 transition-colors duration-200">
                  <Icon name="Download" size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header & Tabs */}
      <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-blue-100">
        <div className="px-6 py-4 border-b border-blue-100">
          <h2 className="text-xl font-semibold text-slate-800 font-heading flex items-center">
            <Icon name="CreditCard" size={20} className="mr-2 text-blue-600" />
            Subscription & Package Management
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Manage your wellness memberships, session packages, and billing information
          </p>
        </div>
        
        <div className="px-6 py-4">
          <div className="flex space-x-1 bg-blue-50 rounded-lg p-1">
            {[
              { id: 'active', label: 'Active Plans', icon: 'CheckCircle' },
              { id: 'packages', label: 'Available Plans', icon: 'Package' },
              { id: 'billing', label: 'Billing History', icon: 'Receipt' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center space-x-2 py-2 px-4 rounded-md text-sm font-medium transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                <Icon name={tab.icon} size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'active' && renderActiveSubscriptions()}
      {activeTab === 'packages' && renderAvailablePackages()}
      {activeTab === 'billing' && renderBillingHistory()}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Cancel Subscription</h3>
            
            <div className="mb-6">
              <p className="text-slate-600 mb-4">
                Are you sure you want to cancel your <strong>{showCancelModal.name}</strong> subscription?
              </p>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-800 mb-2">What happens when you cancel:</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• Your subscription will remain active until {new Date(showCancelModal.nextBilling).toLocaleDateString()}</li>
                  <li>• You'll lose access to member benefits after the current period</li>
                  <li>• Any unused sessions will expire</li>
                  <li>• You can resubscribe anytime</li>
                </ul>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={confirmCancel}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md font-medium hover:bg-red-700 transition-colors duration-200"
              >
                Cancel Subscription
              </button>
              <button
                onClick={() => setShowCancelModal(null)}
                className="flex-1 border border-slate-200 text-slate-600 px-4 py-2 rounded-md font-medium hover:bg-slate-50 transition-colors duration-200"
              >
                Keep Subscription
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Plan Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Upgrade Plan</h3>
            
            <div className="mb-6">
              <p className="text-slate-600 mb-4">
                Upgrade to <strong>{showUpgradeModal.name}</strong>?
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">Plan Benefits:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  {showUpgradeModal.features?.slice(0, 4).map((feature, index) => (
                    <li key={index}>• {feature}</li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={confirmUpgrade}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Upgrade Now
              </button>
              <button
                onClick={() => setShowUpgradeModal(null)}
                className="flex-1 border border-slate-200 text-slate-600 px-4 py-2 rounded-md font-medium hover:bg-slate-50 transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;